import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { 
  Play, 
  Home, 
  Tractor, 
  Bike, 
  Laptop, 
  Tv, 
  ChevronRight,
  ZoomIn,
  Sun,
  Leaf,
  Zap,
  Award,
  Camera,
  Video,
  Share2
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Link } from 'react-router-dom';
import ImageLightbox from '../components/ImageLightbox';
import SocialShareButtons from '../components/SocialShareButtons';

const Prizes = () => {
  const { t, i18n } = useTranslation();
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxImages, setLightboxImages] = useState([]);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  // All text is now managed via i18n translation files
  const text = {
    pageTitle: t('prizesPage.pageTitle'),
    pageSubtitle: t('prizesPage.pageSubtitle'),
    videoTitle: t('prizesPage.videoTitle'),
    videoSubtitle: t('prizesPage.videoSubtitle'),
    videoPlaceholder: t('prizesPage.videoPlaceholder'),
    videoComingSoon: t('prizesPage.videoComingSoon'),
    galleryTitle: t('prizesPage.galleryTitle'),
    viewMore: t('prizesPage.viewMore'),
    specifications: t('prizesPage.specifications'),
    buyTickets: t('nav.buyTickets'),
    totalValue: t('prizesPage.totalValue'),
    clickToZoom: t('prizesPage.clickToZoom'),
    prizeNumber: t('prizesPage.prizeNumber'),
    photos: t('prizesPage.photos'),
    comingSoon: t('prizesPage.comingSoon'),
    ctaTitle: t('prizesPage.ctaTitle'),
    ctaDescription: t('prizesPage.ctaDescription'),
  };

  // Translated specifications for each prize
  const specsTranslations = {
    fr: {
      house: [
        '3 Chambres à coucher',
        '2 Salles de bain',
        'Système solaire 10 kW',
        '6 Électroménagers inclus',
        'Autonomie énergétique',
      ],
      tractor: [
        '180 CV de puissance',
        '4 Roues Motrices (4RM)',
        'Transmission multi-vitesses',
        'Agriculture grande échelle',
      ],
      tricycle: [
        'Moteur 350CC',
        'Toute équipée',
        'Idéal transport marchandises',
      ],
      laptop: [
        'Intel i7-13700H 14 cœurs',
        'Écran 17.3" Full HD tactile',
        '64 Go DDR4 / SSD 4 To',
        'Thunderbolt 4, Wi-Fi 6E',
      ],
      tv: [
        '65 pouces OLED 4K UHD',
        'HDR - Qualité exceptionnelle',
        'Smart TV 2025',
      ],
    },
    en: {
      house: [
        '3 Bedrooms',
        '2 Bathrooms',
        '10 kW Solar System',
        '6 Appliances included',
        'Energy independence',
      ],
      tractor: [
        '180 HP power',
        '4 Wheel Drive (4WD)',
        'Multi-speed transmission',
        'Large-scale agriculture',
      ],
      tricycle: [
        '350CC Engine',
        'Fully equipped',
        'Ideal for goods transport',
      ],
      laptop: [
        'Intel i7-13700H 14 cores',
        '17.3" Full HD touchscreen',
        '64 GB DDR4 / 4 TB SSD',
        'Thunderbolt 4, Wi-Fi 6E',
      ],
      tv: [
        '65-inch OLED 4K UHD',
        'HDR - Exceptional quality',
        'Smart TV 2025',
      ],
    },
    ht: {
      house: [
        '3 Chanm kouche',
        '2 Sal de ben',
        'Sistèm solè 10 kW',
        '6 Elektwomenaje enkli',
        'Otonomi enèjetik',
      ],
      tractor: [
        '180 CV pisans',
        '4 Wou Motris (4RM)',
        'Transmisyon plizyè vitès',
        'Agrikilti gwo echèl',
      ],
      tricycle: [
        'Motè 350CC',
        'Konplètman ekipe',
        'Ideyal pou transpò machandiz',
      ],
      laptop: [
        'Intel i7-13700H 14 nwayo',
        'Ekran 17.3" Full HD taktil',
        '64 Go DDR4 / SSD 4 To',
        'Thunderbolt 4, Wi-Fi 6E',
      ],
      tv: [
        '65 pous OLED 4K UHD',
        'HDR - Kalite eksepsyonèl',
        'Smart TV 2025',
      ],
    },
    es: {
      house: [
        '3 Dormitorios',
        '2 Baños',
        'Sistema solar 10 kW',
        '6 Electrodomésticos incluidos',
        'Independencia energética',
      ],
      tractor: [
        '180 CV de potencia',
        '4 Ruedas Motrices (4RM)',
        'Transmisión multi-velocidades',
        'Agricultura a gran escala',
      ],
      tricycle: [
        'Motor 350CC',
        'Totalmente equipado',
        'Ideal transporte mercancías',
      ],
      laptop: [
        'Intel i7-13700H 14 núcleos',
        'Pantalla 17.3" Full HD táctil',
        '64 GB DDR4 / SSD 4 TB',
        'Thunderbolt 4, Wi-Fi 6E',
      ],
      tv: [
        '65 pulgadas OLED 4K UHD',
        'HDR - Calidad excepcional',
        'Smart TV 2025',
      ],
    },
    pt: {
      house: [
        '3 Quartos',
        '2 Banheiros',
        'Sistema solar 10 kW',
        '6 Eletrodomésticos incluídos',
        'Independência energética',
      ],
      tractor: [
        '180 CV de potência',
        '4 Rodas Motrizes (4RM)',
        'Transmissão multi-velocidades',
        'Agricultura em grande escala',
      ],
      tricycle: [
        'Motor 350CC',
        'Totalmente equipado',
        'Ideal para transporte de mercadorias',
      ],
      laptop: [
        'Intel i7-13700H 14 núcleos',
        'Tela 17.3" Full HD tátil',
        '64 GB DDR4 / SSD 4 TB',
        'Thunderbolt 4, Wi-Fi 6E',
      ],
      tv: [
        '65 polegadas OLED 4K UHD',
        'HDR - Qualidade excepcional',
        'Smart TV 2025',
      ],
    },
  };

  const captionsTranslations = {
    fr: {
      house: ['Vue extérieure de la maison', 'Piscine et terrasse', 'Chambre principale', 'Salon spacieux', 'Salle de bain moderne', 'Salle à manger élégante', 'Salon avec vue', 'Cuisine moderne', 'Salle de bain luxueuse', 'Chambre élégante'],
      tractor: ['Tracteur-Semeur diesel 4RM 180 CV', 'Vue latérale complète', 'Cabine ergonomique', 'Prêt pour le travail agricole'],
      tricycle: ['Tricycle Cargo 350CC', 'Vue complète du véhicule', 'Espace cargo spacieux', 'Robuste et fiable'],
      laptop: ['HP Envy 17.3" - Intel i7', 'Design élégant et fin', 'Utilisation quotidienne', 'Ports et connectivité'],
      tv: ['LG OLED evo AI 4K 65"', 'Smart TV avec Google TV', 'Design ultra-fin', 'Commande vocale IA'],
    },
    en: {
      house: ['Exterior view of the house', 'Pool and terrace', 'Master bedroom', 'Spacious living room', 'Modern bathroom', 'Elegant dining room', 'Living room with view', 'Modern kitchen', 'Luxurious bathroom', 'Elegant bedroom'],
      tractor: ['180 HP 4WD Diesel Seeder Tractor', 'Complete side view', 'Ergonomic cabin', 'Ready for agricultural work'],
      tricycle: ['350CC Cargo Tricycle', 'Complete vehicle view', 'Spacious cargo area', 'Robust and reliable'],
      laptop: ['HP Envy 17.3" - Intel i7', 'Sleek and slim design', 'Daily use', 'Ports and connectivity'],
      tv: ['LG OLED evo AI 4K 65"', 'Smart TV with Google TV', 'Ultra-slim design', 'AI voice control'],
    },
    ht: {
      house: ['Vi deyò kay la', 'Pisin ak teras', 'Chanm prensipal', 'Salon espasye', 'Saldeben modèn', 'Salaman elegan', 'Salon ak vi', 'Kizin modèn', 'Saldeben liks', 'Chanm elegan'],
      tractor: ['Traktè-Semè dyezèl 4RM 180 CV', 'Vi konplè sou kote', 'Kabin egonòmik', 'Pare pou travay agrikòl'],
      tricycle: ['Trisikl Kago 350CC', 'Vi konplè machin nan', 'Espas kago espasye', 'Solid epi fyab'],
      laptop: ['HP Envy 17.3" - Intel i7', 'Design elegán epi fen', 'Itilizasyon chak jou', 'Pò ak koneksyon'],
      tv: ['LG OLED evo AI 4K 65"', 'Smart TV ak Google TV', 'Design iltra-fen', 'Kontwòl vwa IA'],
    },
    es: {
      house: ['Vista exterior de la casa', 'Piscina y terraza', 'Dormitorio principal', 'Sala de estar espaciosa', 'Baño moderno', 'Comedor elegante', 'Sala de estar con vista', 'Cocina moderna', 'Baño de lujo', 'Dormitorio elegante'],
      tractor: ['Tractor-Sembrador diésel 4RM 180 CV', 'Vista lateral completa', 'Cabina ergonómica', 'Listo para el trabajo agrícola'],
      tricycle: ['Triciclo de Carga 350CC', 'Vista completa del vehículo', 'Área de carga espaciosa', 'Robusto y confiable'],
      laptop: ['HP Envy 17.3" - Intel i7', 'Diseño elegante y delgado', 'Uso diario', 'Puertos y conectividad'],
      tv: ['LG OLED evo AI 4K 65"', 'Smart TV con Google TV', 'Diseño ultra delgado', 'Control de voz IA'],
    },
    pt: {
      house: ['Vista exterior da casa', 'Piscina e terraço', 'Quarto principal', 'Sala de estar espaçosa', 'Banheiro moderno', 'Sala de jantar elegante', 'Sala de estar com vista', 'Cozinha moderna', 'Banheiro luxuoso', 'Quarto elegante'],
      tractor: ['Trator-Semeador diesel 4RM 180 CV', 'Vista lateral completa', 'Cabine ergonômica', 'Pronto para o trabalho agrícola'],
      tricycle: ['Triciclo de Carga 350CC', 'Vista completa do veículo', 'Área de carga espaçosa', 'Robusto e confiável'],
      laptop: ['HP Envy 17.3" - Intel i7', 'Design elegante e fino', 'Uso diário', 'Portas e conectividade'],
      tv: ['LG OLED evo AI 4K 65"', 'Smart TV com Google TV', 'Design ultra fino', 'Controle de voz IA'],
    },
  };

  const currentSpecs = specsTranslations[i18n.language] || specsTranslations.fr;
  const currentCaptions = captionsTranslations[i18n.language] || captionsTranslations.fr;

  const prizes = [
    {
      id: 'house',
      title: t('prizes.house.title'),
      value: '$450,000 USD',
      valueNum: 450000,
      icon: Home,
      color: 'from-amber-500 to-yellow-400',
      bgColor: 'bg-amber-500',
      mainImage: '/maison-edenlife.jpeg',
      images: [
        { src: '/maison-edenlife.jpeg', alt: t('prizes.house.title'), caption: currentCaptions.house[0] },
        { src: '/house-pool.jpg', alt: t('prizes.house.title'), caption: currentCaptions.house[1] },
        { src: '/house-new-1.jpg', alt: t('prizes.house.title'), caption: currentCaptions.house[2] },
        { src: '/house-new-2.jpg', alt: t('prizes.house.title'), caption: currentCaptions.house[3] },
        { src: '/house-new-3.jpg', alt: t('prizes.house.title'), caption: currentCaptions.house[4] },
        { src: '/house-new-4.jpg', alt: t('prizes.house.title'), caption: currentCaptions.house[5] },
        { src: '/house-new-5.jpg', alt: t('prizes.house.title'), caption: currentCaptions.house[6] },
        { src: '/house-new-6.jpg', alt: t('prizes.house.title'), caption: currentCaptions.house[7] },
        { src: '/house-new-7.jpg', alt: t('prizes.house.title'), caption: currentCaptions.house[8] },
        { src: '/house-new-8.jpg', alt: t('prizes.house.title'), caption: currentCaptions.house[9] },
      ],
      specs: currentSpecs.house.map((label, i) => ({ 
        icon: [Home, Home, Sun, Zap, Leaf][i], 
        label 
      })),
      description: t('prizes.house.description'),
    },
    {
      id: 'tractor',
      title: t('prizes.tractor.title'),
      value: '$40,000 USD',
      valueNum: 40000,
      icon: Tractor,
      color: 'from-green-600 to-green-400',
      bgColor: 'bg-green-600',
      mainImage: '/tracteur-agricole.png',
      images: [
        { src: '/tracteur-agricole.png', alt: t('prizes.tractor.title'), caption: currentCaptions.tractor[0] },
        { src: '/tractor-3.png', alt: t('prizes.tractor.title'), caption: currentCaptions.tractor[1] },
        { src: '/tractor-4.png', alt: t('prizes.tractor.title'), caption: currentCaptions.tractor[2] },
        { src: '/tractor-5.png', alt: t('prizes.tractor.title'), caption: currentCaptions.tractor[3] },
      ],
      specs: currentSpecs.tractor.map((label, i) => ({ 
        icon: [Zap, Tractor, Leaf, Award][i], 
        label 
      })),
      description: t('prizes.tractor.description'),
    },
    {
      id: 'tricycle',
      title: t('prizes.moto.title'),
      value: '$5,000 USD',
      valueNum: 5000,
      icon: Bike,
      color: 'from-red-600 to-red-400',
      bgColor: 'bg-red-600',
      mainImage: '/tricycle-cargo.jpeg',
      images: [
        { src: '/tricycle-cargo.jpeg', alt: t('prizes.moto.title'), caption: currentCaptions.tricycle[0] },
        { src: '/cargo-3.jpg', alt: t('prizes.moto.title'), caption: currentCaptions.tricycle[1] },
        { src: '/cargo-4.jpg', alt: t('prizes.moto.title'), caption: currentCaptions.tricycle[2] },
        { src: '/cargo-6.jpg', alt: t('prizes.moto.title'), caption: currentCaptions.tricycle[3] },
      ],
      specs: currentSpecs.tricycle.map((label, i) => ({ 
        icon: [Zap, Bike, Award][i], 
        label 
      })),
      description: t('prizes.moto.description'),
    },
    {
      id: 'laptop',
      title: t('prizes.laptop.title'),
      value: '$3,000 USD',
      valueNum: 3000,
      icon: Laptop,
      color: 'from-blue-600 to-blue-400',
      bgColor: 'bg-blue-600',
      mainImage: '/laptop-hp-envy.png',
      images: [
        { src: '/laptop-hp-envy.png', alt: t('prizes.laptop.title'), caption: currentCaptions.laptop[0] },
        { src: '/laptop-2.png', alt: t('prizes.laptop.title'), caption: currentCaptions.laptop[1] },
        { src: '/laptop-3.jpeg', alt: t('prizes.laptop.title'), caption: currentCaptions.laptop[2] },
        { src: '/laptop-4.png', alt: t('prizes.laptop.title'), caption: currentCaptions.laptop[3] },
      ],
      specs: currentSpecs.laptop.map((label, i) => ({ 
        icon: [Zap, Laptop, Award, Zap][i], 
        label 
      })),
      description: t('prizes.laptop.description'),
    },
    {
      id: 'tv',
      title: t('prizes.tv.title'),
      value: '$2,000 USD',
      valueNum: 2000,
      icon: Tv,
      color: 'from-purple-600 to-purple-400',
      bgColor: 'bg-purple-600',
      mainImage: '/tv-lg-oled.jpg',
      images: [
        { src: '/tv-lg-oled.jpg', alt: t('prizes.tv.title'), caption: currentCaptions.tv[0] },
        { src: '/tv-2.jpg', alt: t('prizes.tv.title'), caption: currentCaptions.tv[1] },
        { src: '/tv-3.jpg', alt: t('prizes.tv.title'), caption: currentCaptions.tv[2] },
        { src: '/tv-4.jpg', alt: t('prizes.tv.title'), caption: currentCaptions.tv[3] },
      ],
      specs: currentSpecs.tv.map((label, i) => ({ 
        icon: [Tv, Zap, Award][i], 
        label 
      })),
      description: t('prizes.tv.description'),
    },
  ];

  const totalValue = prizes.reduce((sum, prize) => sum + prize.valueNum, 0);

  const openLightbox = (images, index = 0) => {
    setLightboxImages(images);
    setLightboxIndex(index);
    setLightboxOpen(true);
  };

  return (
    <div className="min-h-screen bg-gray-50 pt-24 pb-16">
      {/* Hero Section */}
      <section className="relative py-16 px-6 bg-gradient-to-br from-primary via-green-600 to-accent text-white overflow-hidden">
        <div className="absolute inset-0 bg-black/20"></div>
        <div className="max-w-6xl mx-auto relative z-10 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <span className="inline-block bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full text-sm font-medium mb-6">
              🏆 Marathon de l'Avenir EDENLIFE 2026
            </span>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
              {text.pageTitle}
            </h1>
            <p className="text-xl text-white/90 max-w-3xl mx-auto mb-8">
              {text.pageSubtitle}
            </p>
            
            {/* Total Value Badge */}
            <div className="inline-flex items-center gap-3 bg-white/20 backdrop-blur-sm rounded-2xl px-6 py-4">
              <Award className="w-8 h-8 text-yellow-300" />
              <div className="text-left">
                <p className="text-sm text-white/80">{text.totalValue}</p>
                <p className="text-3xl font-bold text-yellow-300">${totalValue.toLocaleString()} USD</p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Video Section */}
      <section className="py-16 px-6 bg-white">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <span className="inline-flex items-center gap-2 bg-primary/10 text-primary px-4 py-2 rounded-full text-sm font-medium mb-4">
              <Video className="w-4 h-4" />
              {text.videoTitle}
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900">
              {text.videoSubtitle}
            </h2>
          </motion.div>

          {/* Video Player */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="relative aspect-video bg-black rounded-2xl overflow-hidden shadow-2xl"
          >
            <video
              className="w-full h-full object-cover"
              controls
              poster="/maison-edenlife.jpeg"
              preload="metadata"
            >
              <source src="/promo-video.mp4" type="video/mp4" />
              Votre navigateur ne supporte pas la lecture vidéo.
            </video>
          </motion.div>
        </div>
      </section>

      {/* Prizes Gallery */}
      <section className="py-16 px-6">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <span className="inline-flex items-center gap-2 bg-amber-100 text-amber-700 px-4 py-2 rounded-full text-sm font-medium mb-4">
              <Camera className="w-4 h-4" />
              {text.galleryTitle}
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900">
              {text.pageTitle}
            </h2>
          </motion.div>

          <div className="space-y-16">
            {prizes.map((prize, index) => (
              <motion.div
                key={prize.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className={`flex flex-col ${index % 2 === 1 ? 'lg:flex-row-reverse' : 'lg:flex-row'} gap-8 items-center`}
              >
                {/* Image Gallery */}
                <div className="w-full lg:w-1/2">
                  <div 
                    className="relative group cursor-pointer"
                    onClick={() => openLightbox(prize.images, 0)}
                  >
                    <img
                      src={prize.mainImage}
                      alt={prize.title}
                      className="w-full h-80 object-cover rounded-2xl shadow-xl transition-transform group-hover:scale-[1.02]"
                    />
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors rounded-2xl flex items-center justify-center pointer-events-none">
                      <div className="opacity-0 group-hover:opacity-100 transition-opacity bg-white/90 backdrop-blur-sm px-4 py-2 rounded-full flex items-center gap-2">
                        <ZoomIn className="w-5 h-5" />
                        <span className="font-medium">{text.clickToZoom}</span>
                      </div>
                    </div>
                    {/* Prize badge */}
                    <div className={`absolute top-4 left-4 ${prize.bgColor} text-white px-4 py-2 rounded-full font-bold shadow-lg`}>
                      {prize.value}
                    </div>
                    {/* Image count */}
                    <div className="absolute bottom-4 right-4 bg-black/60 text-white px-3 py-1 rounded-full text-sm flex items-center gap-2">
                      <Camera className="w-4 h-4" />
                      {prize.images.length} {text.photos}
                    </div>
                  </div>
                  
                  {/* Thumbnail strip */}
                  {prize.images.length > 1 && (
                    <div className="flex gap-2 mt-4 overflow-x-auto pb-2">
                      {prize.images.slice(0, 4).map((img, imgIndex) => (
                        <img
                          key={imgIndex}
                          src={img.src}
                          alt={img.alt}
                          className="w-20 h-20 object-cover rounded-lg cursor-pointer opacity-70 hover:opacity-100 transition-opacity flex-shrink-0"
                          onClick={() => openLightbox(prize.images, imgIndex)}
                        />
                      ))}
                      {prize.images.length > 4 && (
                        <div 
                          className="w-20 h-20 bg-gray-200 rounded-lg flex items-center justify-center cursor-pointer hover:bg-gray-300 transition-colors flex-shrink-0"
                          onClick={() => openLightbox(prize.images, 4)}
                        >
                          <span className="text-gray-600 font-medium">+{prize.images.length - 4}</span>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Prize Details */}
                <div className="w-full lg:w-1/2">
                  <div className={`inline-flex items-center gap-2 bg-gradient-to-r ${prize.color} text-white px-4 py-2 rounded-full mb-4`}>
                    <prize.icon className="w-5 h-5" />
                    <span className="font-medium">{text.prizeNumber}{index + 1}</span>
                  </div>
                  
                  <h3 className="text-3xl font-bold text-gray-900 mb-4">
                    {prize.title}
                  </h3>
                  
                  <p className="text-gray-600 text-lg mb-6 leading-relaxed">
                    {prize.description}
                  </p>

                  {/* Specifications */}
                  <div className="bg-gray-50 rounded-xl p-6 mb-6">
                    <h4 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                      <Award className="w-5 h-5 text-primary" />
                      {text.specifications}
                    </h4>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {prize.specs.map((spec, specIndex) => (
                        <div key={specIndex} className="flex items-center gap-3 text-gray-700">
                          <spec.icon className="w-5 h-5 text-primary flex-shrink-0" />
                          <span>{spec.label}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center">
                    <Link to="/purchase">
                      <Button className={`bg-gradient-to-r ${prize.color} hover:opacity-90 text-white px-8 py-3 rounded-full font-semibold`}>
                        {text.buyTickets}
                        <ChevronRight className="w-5 h-5 ml-2" />
                      </Button>
                    </Link>
                    
                    {/* Share buttons for each prize */}
                    <div className="flex items-center gap-2">
                      <Share2 className="w-4 h-4 text-gray-500" />
                      <SocialShareButtons 
                        url={`${window.location.origin}/prizes#${prize.id}`}
                        title={`${prize.title} - ${prize.value} | Marathon de l'Avenir EDENLIFE 2026`}
                        description={prize.description}
                        image={prize.mainImage}
                        size="sm"
                        showLabel={false}
                      />
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Share CTA Section */}
      <section className="py-12 px-6 bg-gradient-to-r from-blue-600 to-purple-600">
        <div className="max-w-4xl mx-auto text-center text-white">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <Share2 className="w-12 h-12 mx-auto mb-4 opacity-80" />
            <h2 className="text-2xl md:text-3xl font-bold mb-4">
              {t('share.helpUsReach')}
            </h2>
            <p className="text-white/80 mb-6 max-w-2xl mx-auto">
              {t('share.sharePrizesPage')}
            </p>
            <div className="flex justify-center">
              <SocialShareButtons 
                title={t('share.shareTitle')}
                description={t('share.shareDescription')}
                size="lg"
                showLabel={false}
              />
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 px-6 bg-gradient-to-r from-primary to-accent">
        <div className="max-w-4xl mx-auto text-center text-white">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              {text.ctaTitle}
            </h2>
            <p className="text-xl text-white/90 mb-8">
              {text.ctaDescription}
            </p>
            <Link to="/purchase">
              <Button className="bg-white text-primary hover:bg-gray-100 px-10 py-4 rounded-full font-bold text-lg">
                🎟️ {text.buyTickets}
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Lightbox */}
      <ImageLightbox
        images={lightboxImages}
        initialIndex={lightboxIndex}
        isOpen={lightboxOpen}
        onClose={() => setLightboxOpen(false)}
      />
    </div>
  );
};

export default Prizes;
